import React from 'react';
import {
  Box, Grid, Button, Divider, CircularProgress, Typography,
  IconButton, TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { postRequest } from 'Includes/api/apicall';
import { POST_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
function ExpenseTypeAdd(props) {
  const [loading, setLoading] = React.useState(false);
  const [submitDisable, setSubmitDisable] = React.useState(false);
  const navigate = useNavigate();
  // Dynamic expense types state
  const [expenseTypes, setExpenseTypes] = React.useState([{ id: 0 }]);
  const [expenseTypeDetails, setExpenseTypeDetails] = React.useState({
    0: {
      code: '',
      name: '',
      description: '',
    }
  });
  const [expenseTypeErrors, setExpenseTypeErrors] = React.useState({});
  const sectionTitleSx = {
    mb: 2.5,
    pl: 1.75,
    borderLeft: '4px solid #4680FF',
    color: '#4680FF',
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.05rem' },
    lineHeight: 1.35,
  };
  const sectionPaperSx = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  };

  // Add new expense type
  const addExpenseType = () => {
    const newId = expenseTypes.length > 0 ? Math.max(...expenseTypes.map(et => et.id)) + 1 : 0;
    setExpenseTypes([...expenseTypes, { id: newId }]);
    setExpenseTypeDetails(prev => ({
      ...prev,
      [newId]: {
        code: '',
        name: '',
        description: '',
      }
    }));
  };

  // Remove expense type
  const removeExpenseType = (expenseTypeId) => {
    if (expenseTypes.length > 1) {
      setExpenseTypes(expenseTypes.filter(et => et.id !== expenseTypeId));
      const newExpenseTypeDetails = { ...expenseTypeDetails };
      delete newExpenseTypeDetails[expenseTypeId];
      setExpenseTypeDetails(newExpenseTypeDetails);
      
      // Clear errors for removed expense type
      const newErrors = { ...expenseTypeErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${expenseTypeId}_`)) {
          delete newErrors[key];
        }
      });
      setExpenseTypeErrors(newErrors);
    }
  };

  // Handle field change
  const handleFieldChange = (expenseTypeId, fieldName, value) => {
    setExpenseTypeDetails(prev => ({
      ...prev,
      [expenseTypeId]: {
        ...prev[expenseTypeId],
        [fieldName]: value
      }
    }));
    
    // Clear error for this field
    setExpenseTypeErrors(prev => ({
      ...prev,
      [`${expenseTypeId}_${fieldName}`]: ''
    }));
  };

  // Validation
  const validate = () => {
    const errors = {};
    let isValid = true;

    expenseTypes.forEach((expenseType) => {
      const data = expenseTypeDetails[expenseType.id];
      
      if (!data.code || data.code.trim() === '') {
        errors[`${expenseType.id}_code`] = 'Code is required';
        isValid = false;
      } else {
        // Validate code format (slug format - lowercase, alphanumeric, hyphens, underscores)
        const codeRegex = /^[a-z0-9_-]+$/;
        if (!codeRegex.test(data.code.trim())) {
          errors[`${expenseType.id}_code`] = 'Code must be lowercase alphanumeric with hyphens or underscores only';
          isValid = false;
        }
      }
      
      if (!data.name || data.name.trim() === '') {
        errors[`${expenseType.id}_name`] = 'Name is required';
        isValid = false;
      }
    });

    setExpenseTypeErrors(errors);
    return isValid;
  };

  // Submit
  const submit = () => {
    if (validate()) {
      setSubmitDisable(true);
      
      // Prepare data
      const expenseTypesData = expenseTypes.map((expenseType) => ({
        code: expenseTypeDetails[expenseType.id].code.trim(),
        name: expenseTypeDetails[expenseType.id].name.trim(),
        description: expenseTypeDetails[expenseType.id].description.trim() || '',
      }));

      const url = POST_URL.expense_types.api;
      postRequest(url, expenseTypesData, props).then(response => {
        setSubmitDisable(false);
        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: response.data.Reason || 'Success',
            text: 'Expense Type(s) added successfully',
            showConfirmButton: false,
            timer: 1500
          });
          navigate('/expenses/expense-type/list');
        }
      }).catch((error) => {
        setSubmitDisable(false);
        const errorMessage = error?.response?.data?.Reason || error?.response?.data?.code?.[0] || 'Failed to create expense type(s)';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          timer: 2000,
          showConfirmButton: false
        });
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setExpenseTypes([{ id: 0 }]);
    setExpenseTypeDetails({
      0: {
        code: '',
        name: '',
        description: '',
      }
    });
    setExpenseTypeErrors({});
  };

  return (
    <Box p={2}>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <Box>
          <Grid container alignItems="center" spacing={2}>
            <Grid item md={6} xs={12} mt={2}>
              <Typography variant="h5" fontWeight={600}>
                Expense Type Informations
              </Typography>
            </Grid>
            <Grid item md={6} xs={12}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/expenses/expense-type/list')}
                  sx={{ textTransform: 'none' }}
                >
                  <ArrowBackOutlinedIcon sx={{ mr: 1 }} />
                  View Expense Types
                </Button>
                <IconButton color="primary" onClick={addExpenseType} size="small" title="Add another expense type">
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
          <Box mt={3} mb={3}><Divider /></Box>

          <Box sx={sectionPaperSx}>
            <Typography variant="subtitle1" component="h2" sx={sectionTitleSx}>
              Expense Type Details
            </Typography>
          {expenseTypes.map((expenseType, index) => (
            <Box key={expenseType.id} mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Expense Type {index + 1}
                </Typography>
                {expenseTypes.length > 1 && (
                  <IconButton
                    color="secondary"
                    onClick={() => removeExpenseType(expenseType.id)}
                    size="small"
                    title="Remove this expense type"
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
              
              <Grid container spacing={2}>
                {/* Code */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Code"
                    variant="outlined"
                    fullWidth
                    required
                    value={expenseTypeDetails[expenseType.id]?.code || ''}
                    onChange={(e) => handleFieldChange(expenseType.id, 'code', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                    error={!!expenseTypeErrors[`${expenseType.id}_code`]}
                    helperText={expenseTypeErrors[`${expenseType.id}_code`] || 'Lowercase alphanumeric with hyphens or underscores'}
                    inputProps={{ maxLength: 64 }}
                  />
                </Grid>

                {/* Name */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={expenseTypeDetails[expenseType.id]?.name || ''}
                    onChange={(e) => handleFieldChange(expenseType.id, 'name', e.target.value)}
                    error={!!expenseTypeErrors[`${expenseType.id}_name`]}
                    helperText={expenseTypeErrors[`${expenseType.id}_name`] || ''}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={2}
                    value={expenseTypeDetails[expenseType.id]?.description || ''}
                    onChange={(e) => handleFieldChange(expenseType.id, 'description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

            <Box mt={3} mb={3}><Divider /></Box>
            <Box display='flex' justifyContent='flex-end' gap={2} className='mv-20'>
            <Button
              variant='outlined'
              onClick={resetForm}
            >
              Reset
            </Button>
            <Button
              variant='contained'
              color='primary'
              className='submit'
              disabled={submitDisable}
              onClick={submit}
            >
              {submitDisable ? 'Submitting...' : 'Submit'}
            </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ExpenseTypeAdd;

